import type { WorkOrder, WOStatus, Priority } from '@/types/workOrder';

// Baseline computed once per session — keeps countdowns stable during a demo
// while still feeling current.
const SESSION_NOW = Date.now();

const day = (n: number) => SESSION_NOW + n * 24 * 3600 * 1000;
const hour = (n: number) => SESSION_NOW + n * 3600 * 1000;

interface WOSeed {
  id: string;
  status: WOStatus;
  priority: Priority;
  customerId: string;
  customerName: string;
  productSkuId: string;
  productDescription: string;
  qtyPlanned: number;
  qtyProduced: number;
  qtyScrap: number;
  lineId: string;
  currentStationId: string;
  currentOperatorId: string;
  currentOperationIndex: number;
  costPlanned: number;
  costActual: number;
  plannedStart: number;          // ms offset from now
  plannedEnd: number;
  actualStart?: number;
  actualEnd?: number;
  due: number;
  lastEvent: number;
}

const SEEDS: WOSeed[] = [
  // ---- LINE-A · solar (Acme + Solartech) ----
  {
    id: 'WO-2026-0042',
    status: 'in_progress',
    priority: 1,
    customerId: 'CUST-ACME', customerName: 'Acme S.L.',
    productSkuId: 'SKU-PV400', productDescription: 'Panel Solar 400W Monocristalino',
    qtyPlanned: 500, qtyProduced: 247, qtyScrap: 8,
    lineId: 'LINE-A', currentStationId: 'ST-A3', currentOperatorId: 'EMP-1042', currentOperationIndex: 3,
    costPlanned: 18500, costActual: 9450,
    plannedStart: -6 * 24 * 3600 * 1000, plannedEnd: 5 * 24 * 3600 * 1000,
    actualStart: -6 * 24 * 3600 * 1000,
    due: 5 * 24 * 3600 * 1000, lastEvent: -0.5 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0043',
    status: 'in_progress',
    priority: 2,
    customerId: 'CUST-SOLT', customerName: 'Solartech Iberia',
    productSkuId: 'SKU-PV450', productDescription: 'Panel Solar 450W Bifacial',
    qtyPlanned: 300, qtyProduced: 90, qtyScrap: 4,
    lineId: 'LINE-A', currentStationId: 'ST-A2', currentOperatorId: 'EMP-1051', currentOperationIndex: 2,
    costPlanned: 14200, costActual: 4600,
    plannedStart: -3 * 24 * 3600 * 1000, plannedEnd: 6 * 24 * 3600 * 1000,
    actualStart: -3 * 24 * 3600 * 1000,
    due: 6 * 24 * 3600 * 1000, lastEvent: -1.2 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0044',
    status: 'released',
    priority: 2,
    customerId: 'CUST-ACME', customerName: 'Acme S.L.',
    productSkuId: 'SKU-PV400', productDescription: 'Panel Solar 400W Monocristalino',
    qtyPlanned: 200, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-A', currentStationId: 'ST-A1', currentOperatorId: 'EMP-1042', currentOperationIndex: 0,
    costPlanned: 7400, costActual: 0,
    plannedStart: 1 * 24 * 3600 * 1000, plannedEnd: 9 * 24 * 3600 * 1000,
    due: 10 * 24 * 3600 * 1000, lastEvent: -8 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0045',
    status: 'completed',
    priority: 3,
    customerId: 'CUST-SOLT', customerName: 'Solartech Iberia',
    productSkuId: 'SKU-INV5K', productDescription: 'Inversor String 5kW',
    qtyPlanned: 60, qtyProduced: 60, qtyScrap: 1,
    lineId: 'LINE-A', currentStationId: 'ST-A4', currentOperatorId: 'EMP-1051', currentOperationIndex: 6,
    costPlanned: 9800, costActual: 10250,
    plannedStart: -14 * 24 * 3600 * 1000, plannedEnd: -2 * 24 * 3600 * 1000,
    actualStart: -14 * 24 * 3600 * 1000, actualEnd: -1.5 * 24 * 3600 * 1000,
    due: -1 * 24 * 3600 * 1000, lastEvent: -1.5 * 24 * 3600 * 1000,
  },

  // ---- LINE-B · elevators (Otis + Schindler) ----
  {
    id: 'WO-2026-0046',
    status: 'in_progress',
    priority: 1,
    customerId: 'CUST-OTIS', customerName: 'Otis Elevator Co.',
    productSkuId: 'SKU-ELV-M1', productDescription: 'Módulo Elevación 1000kg',
    qtyPlanned: 40, qtyProduced: 22, qtyScrap: 1,
    lineId: 'LINE-B', currentStationId: 'ST-B2', currentOperatorId: 'EMP-1063', currentOperationIndex: 4,
    costPlanned: 28000, costActual: 16800,
    plannedStart: -8 * 24 * 3600 * 1000, plannedEnd: 4 * 24 * 3600 * 1000,
    actualStart: -8 * 24 * 3600 * 1000,
    due: 3 * 24 * 3600 * 1000, lastEvent: -0.2 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0047',
    status: 'released',
    priority: 1,
    customerId: 'CUST-SCHN', customerName: 'Schindler Group',
    productSkuId: 'SKU-ELV-M2', productDescription: 'Módulo Elevación 1600kg',
    qtyPlanned: 25, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-B', currentStationId: 'ST-B1', currentOperatorId: 'EMP-1064', currentOperationIndex: 0,
    costPlanned: 19500, costActual: 0,
    plannedStart: 0.5 * 24 * 3600 * 1000, plannedEnd: 9 * 24 * 3600 * 1000,
    due: 8 * 24 * 3600 * 1000, lastEvent: -4 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0048',
    status: 'planned',
    priority: 2,
    customerId: 'CUST-OTIS', customerName: 'Otis Elevator Co.',
    productSkuId: 'SKU-CAB-A', productDescription: 'Cabina Estándar Aluminio',
    qtyPlanned: 50, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-B', currentStationId: 'ST-B3', currentOperatorId: 'EMP-1063', currentOperationIndex: 0,
    costPlanned: 22000, costActual: 0,
    plannedStart: 3 * 24 * 3600 * 1000, plannedEnd: 12 * 24 * 3600 * 1000,
    due: 14 * 24 * 3600 * 1000, lastEvent: -2 * 24 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0049',
    status: 'completed',
    priority: 2,
    customerId: 'CUST-SCHN', customerName: 'Schindler Group',
    productSkuId: 'SKU-ELV-M1', productDescription: 'Módulo Elevación 1000kg',
    qtyPlanned: 30, qtyProduced: 30, qtyScrap: 0,
    lineId: 'LINE-B', currentStationId: 'ST-B4', currentOperatorId: 'EMP-1064', currentOperationIndex: 6,
    costPlanned: 21000, costActual: 20100,
    plannedStart: -20 * 24 * 3600 * 1000, plannedEnd: -3 * 24 * 3600 * 1000,
    actualStart: -20 * 24 * 3600 * 1000, actualEnd: -3 * 24 * 3600 * 1000,
    due: -2 * 24 * 3600 * 1000, lastEvent: -3 * 24 * 3600 * 1000,
  },

  // ---- LINE-C · misc ----
  {
    id: 'WO-2026-0050',
    status: 'released',
    priority: 3,
    customerId: 'CUST-ULLW', customerName: 'Ullswater Investments S.L.',
    productSkuId: 'SKU-MISC-1', productDescription: 'Bastidor Universal 600x400',
    qtyPlanned: 150, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-C', currentStationId: 'ST-C1', currentOperatorId: 'EMP-1071', currentOperationIndex: 0,
    costPlanned: 5400, costActual: 0,
    plannedStart: 1 * 24 * 3600 * 1000, plannedEnd: 4 * 24 * 3600 * 1000,
    due: 6 * 24 * 3600 * 1000, lastEvent: -6 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0051',
    status: 'on_hold',
    priority: 2,
    customerId: 'CUST-AIRB', customerName: 'Airbus Components',
    productSkuId: 'SKU-MISC-2', productDescription: 'Conector Industrial M16 IP67',
    qtyPlanned: 800, qtyProduced: 320, qtyScrap: 14,
    lineId: 'LINE-C', currentStationId: 'ST-C2', currentOperatorId: 'EMP-1072', currentOperationIndex: 2,
    costPlanned: 6200, costActual: 2700,
    plannedStart: -5 * 24 * 3600 * 1000, plannedEnd: 2 * 24 * 3600 * 1000,
    actualStart: -5 * 24 * 3600 * 1000,
    due: 1 * 24 * 3600 * 1000, lastEvent: -10 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0052',
    status: 'released',
    priority: 3,
    customerId: 'CUST-ACME', customerName: 'Acme S.L.',
    productSkuId: 'SKU-MISC-1', productDescription: 'Bastidor Universal 600x400',
    qtyPlanned: 100, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-C', currentStationId: 'ST-C1', currentOperatorId: 'EMP-1071', currentOperationIndex: 0,
    costPlanned: 3600, costActual: 0,
    plannedStart: 2 * 24 * 3600 * 1000, plannedEnd: 5 * 24 * 3600 * 1000,
    due: 7 * 24 * 3600 * 1000, lastEvent: -12 * 3600 * 1000,
  },
  {
    id: 'WO-2026-0053',
    status: 'cancelled',
    priority: 3,
    customerId: 'CUST-ULLW', customerName: 'Ullswater Investments S.L.',
    productSkuId: 'SKU-MISC-2', productDescription: 'Conector Industrial M16 IP67',
    qtyPlanned: 200, qtyProduced: 0, qtyScrap: 0,
    lineId: 'LINE-C', currentStationId: 'ST-C2', currentOperatorId: 'EMP-1072', currentOperationIndex: 0,
    costPlanned: 1500, costActual: 220,
    plannedStart: -4 * 24 * 3600 * 1000, plannedEnd: 3 * 24 * 3600 * 1000,
    due: 4 * 24 * 3600 * 1000, lastEvent: -2 * 3600 * 1000,
  },
];

function fromSeed(s: WOSeed): WorkOrder {
  return {
    id: s.id,
    status: s.status,
    customerId: s.customerId,
    customerName: s.customerName,
    productSkuId: s.productSkuId,
    productDescription: s.productDescription,
    qtyPlanned: s.qtyPlanned,
    qtyProduced: s.qtyProduced,
    qtyScrap: s.qtyScrap,
    plannedStartAt: new Date(SESSION_NOW + s.plannedStart).toISOString(),
    plannedEndAt: new Date(SESSION_NOW + s.plannedEnd).toISOString(),
    actualStartAt: s.actualStart !== undefined ? new Date(SESSION_NOW + s.actualStart).toISOString() : undefined,
    actualEndAt: s.actualEnd !== undefined ? new Date(SESSION_NOW + s.actualEnd).toISOString() : undefined,
    dueDate: new Date(SESSION_NOW + s.due).toISOString(),
    priority: s.priority,
    currentOperationIndex: s.currentOperationIndex,
    currentStationId: s.currentStationId,
    currentOperatorId: s.currentOperatorId,
    lineId: s.lineId,
    costPlanned: s.costPlanned,
    costActual: s.costActual,
    lastEventAt: new Date(SESSION_NOW + s.lastEvent).toISOString(),
  };
}

export const mockWorkOrders: WorkOrder[] = SEEDS.map(fromSeed);

export function getWorkOrder(id: string): WorkOrder | undefined {
  return mockWorkOrders.find((w) => w.id.toUpperCase() === id.toUpperCase());
}

export function listWorkOrders(): WorkOrder[] {
  return mockWorkOrders;
}

export const __SESSION_NOW = SESSION_NOW;
export { day as __day, hour as __hour };
